/**
 * 爱微圈导航 · 分页 / 快捷方式数据加载
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export async function loadShortcutsRaw() {
    return await apiGet(API.shortcuts + '?action=list&page=' + state.currentPage);
}

export function getPageCapacity() {
    const cols = parseInt(state.settings.grid_cols) || 6;
    const rows = parseInt(state.settings.grid_rows) || 3;
    return cols * rows;
}

/**
 * 删除某页后，本地同步收缩页号（与后端 delete_page 的 page-1 重编号保持一致）：
 * 移除该页、更高页号全部减 1，缓存/_newPages/当前页号同步平移。
 */

export function shiftPagesAfterRemoval(deletedPage) {
    state.pages = state.pages.filter(x => x !== deletedPage).map(x => x > deletedPage ? x - 1 : x);
    const cache = {};
    for (const k of Object.keys(state.shortcutCache)) {
        const n = +k;
        if (n === deletedPage) continue;
        cache[n > deletedPage ? n - 1 : n] = state.shortcutCache[k];
    }
    state.shortcutCache = cache;
    if (state.currentPage > deletedPage) state.currentPage--;
    const np = new Set();
    state._newPages.forEach(x => { if (x !== deletedPage) np.add(x > deletedPage ? x - 1 : x); });
    state._newPages = np;
}

/**
 * 强制单页容量：超出网格容量的快捷方式移到下一页。
 * 返回截断后的列表（已符合容量），并异步同步到后端。
 * 注意：卡片模式不走此逻辑（卡片模式一次性展示全部）。
 */

export async function enforcePageCapacity(list, page) {
    const cap = getPageCapacity();
    if (state.settings.display_mode === 'card' || cap <= 0 || list.length <= cap) return list;
    const overflow = list.slice(cap);
    const kept = list.slice(0, cap);
    let nextP = page + 1;
    while (overflow.length > 0) {
        const batch = overflow.splice(0, cap);
        try {
            await apiPost(API.shortcuts + '?action=reorder', {
                items: batch.map((s, idx) => ({ id: s.id, position: idx, page: nextP }))
            });
            if (!state.pages.includes(nextP)) state.pages.push(nextP);
            state.shortcutCache[nextP] = batch;
        } catch (e) {
            // 同步失败时保留原数据，避免界面和 DB 严重不一致
            return list;
        }
        nextP++;
    }
    state.shortcutCache[page] = kept;
    return kept;
}


export function renderPages() {
    const wrap = $('#pageIndicator');
    wrap.innerHTML = '';
    const pages = [...state.pages];
    if (!pages.includes(1)) pages.unshift(1);
    pages.sort((a, b) => a - b);
    pages.forEach(p => {
        const dot = document.createElement('div');
        dot.className = 'page-dot' + (p === state.currentPage ? ' active' : '');
        dot.onclick = () => switchPage(p);
        dot.onmouseenter = () => prefetchPage(p);
        // 非首页支持右键删除
        dot.oncontextmenu = (e) => {
            e.preventDefault();
            if (p === 1) { toast('首页不可删除', 'info'); return; }
            modalConfirm('删除页面', `确认删除第 ${p} 页？页面上的快捷方式将一并删除。`).then(async ok => {
                if (!ok) return;
                await apiPost(API.shortcuts + '?action=delete_page', { page: p });
                const wasCurrent = state.currentPage === p;
                shiftPagesAfterRemoval(p);
                if (wasCurrent) {
                    await switchPage(Math.max(1, p - 1));
                } else {
                    renderPages();
                }
                toast('页面已删除', 'success');
            });
        };
        wrap.appendChild(dot);
    });
    const addDot = document.createElement('div');
    addDot.className = 'page-dot add-page';
    addDot.onclick = async () => {
        const maxPage = pages.length ? pages[pages.length - 1] : 1;
        state._newPages.add(maxPage + 1); // 标记为本次会话新建页（供空页自动清理判断）
        await switchPage(maxPage + 1);
        toast('已新建页面', 'success');
    };
    wrap.appendChild(addDot);

    // 整理按钮：进入/退出批量编辑模式（卡片模式布局复杂，暂不开放）
    if (state.settings.display_mode !== 'card') {
        const orgBtn = document.createElement('div');
        orgBtn.className = 'organize-btn' + (state.organizeMode ? ' active' : '');
        orgBtn.title = state.organizeMode ? '完成整理' : '整理';
        orgBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
        orgBtn.onclick = () => toggleOrganizeMode();
        wrap.appendChild(orgBtn);
    }

    // 同步翻页箭头禁用态，避免点到尽头还出发无意义的 switchPage
    updatePageArrows();
}

export function updatePageArrows() {
    const idx = state.pages.indexOf(state.currentPage);
    const prev = $('#pagePrev'), next = $('#pageNext');
    // 上一页到头就是死路（只会弹 toast），置灰掉
    if (prev) prev.classList.toggle('is-disabled', idx <= 0);
    // 下一页到头是「新建一页」，属于既有能力，不能置灰；只改提示文案
    if (next) {
        const isLast = idx < 0 || idx >= state.pages.length - 1;
        next.title = isLast ? '新建页面' : '下一页';
    }
}
/* 翻页入口集中在这里：鼠标箭头、键盘 ←/→ 共用同一套逻辑，避免三处各写一遍而行为不一致。
 * goNextPage 在最后一页时新建一页（与「+」号一致：登记进 _newPages，
 * 建完不放东西就切走会被自动清理，不会残留空页）。 */

export async function goPrevPage() {
    const idx = state.pages.indexOf(state.currentPage);
    if (idx > 0) return switchPage(state.pages[idx - 1]);
    toast('已经是第一页了');
}

export async function goNextPage() {
    const pages = state.pages;
    const idx = pages.indexOf(state.currentPage);
    if (idx >= 0 && idx < pages.length - 1) return switchPage(pages[idx + 1]);
    const newPage = (pages.length ? pages[pages.length - 1] : 1) + 1;
    state._newPages.add(newPage);
    await switchPage(newPage);
    toast('已新建页面', 'success');
}

export function toggleOrganizeMode(force) {
    const next = typeof force === 'boolean' ? force : !state.organizeMode;
    state.organizeMode = next;
    state.selectedIds = new Set();
    document.body.classList.toggle('organize-mode', state.organizeMode);
    const toolbar = $('#batchToolbar');
    if (toolbar) toolbar.classList.toggle('show', state.organizeMode);
    updateBatchToolbar();
    renderShortcuts();
    renderPages();
}

export function updateBatchToolbar() {
    const toolbar = $('#batchToolbar');
    if (!toolbar) return;
    const info = $('#batchInfo');
    if (info) info.textContent = `已选 ${state.selectedIds.size} 个`;
    const sel = $('#batchMovePage');
    if (sel) {
        const cur = state.currentPage;
        const pages = [...state.pages].sort((a, b) => a - b);
        sel.innerHTML = '<option value="">移动到…</option>' +
            pages.filter(p => p !== cur).map(p => `<option value="${p}">第 ${p} 页</option>`).join('') +
            '<option value="new">新建页</option>';
    }
}

export async function batchDelete() {
    if (state.selectedIds.size === 0) { toast('请先选择要删除的快捷方式', 'info'); return; }
    const ok = await modalConfirm('批量删除', `确认删除选中的 ${state.selectedIds.size} 个快捷方式？`);
    if (!ok) return;
    try {
        await apiPost(API.shortcuts + '?action=batch_delete', { ids: Array.from(state.selectedIds) });
        state.selectedIds = new Set();
        state.shortcutCache = {};
        await loadShortcuts();
        toast('已删除', 'success');
        if (state.selectedIds.size === 0) toggleOrganizeMode(false);
    } catch (e) { toast(e.message, 'error'); }
}

export async function batchMoveToPage(page) {
    if (state.selectedIds.size === 0) { toast('请先选择要移动的快捷方式', 'info'); return; }
    let target = parseInt(page);
    if (page === 'new') {
        const pages = [...state.pages].sort((a, b) => a - b);
        target = (pages[pages.length - 1] || 1) + 1;
    }
    if (!target || target === state.currentPage) return;
    try {
        await apiPost(API.shortcuts + '?action=batch_move_page', {
            ids: Array.from(state.selectedIds),
            page: target,
        });
        state.selectedIds = new Set();
        state.shortcutCache = {};
        await switchPage(target);
        toast(`已移动到第 ${target} 页`, 'success');
    } catch (e) { toast(e.message, 'error'); }
}

export function initBatchToolbar() {
    const toolbar = $('#batchToolbar');
    if (!toolbar || toolbar._bound) return;
    toolbar._bound = true;
    $('#batchSelectAll').onclick = () => {
        state.shortcuts.forEach(s => state.selectedIds.add(s.id));
        updateBatchToolbar();
        renderShortcuts();
    };
    $('#batchSelectNone').onclick = () => {
        state.selectedIds = new Set();
        updateBatchToolbar();
        renderShortcuts();
    };
    $('#batchMovePage').onchange = (e) => {
        const v = e.target.value;
        e.target.value = '';
        if (v) batchMoveToPage(v);
    };
    $('#batchDelete').onclick = batchDelete;
    $('#batchDone').onclick = () => toggleOrganizeMode(false);
}

export function prefetchPage(p) {
    if (!p || state.shortcutCache[p] || state._prefetchingPage === p) return;
    state._prefetchingPage = p;
    apiGet(API.shortcuts + '?action=list&page=' + p)
        .then(list => { state.shortcutCache[p] = list || []; })
        .catch(() => {})
        .finally(() => { state._prefetchingPage = null; });
}

export function prefetchAdjacentPages() {
    if (!state.pages || state.pages.length <= 1) return;
    const idx = state.pages.indexOf(state.currentPage);
    if (idx > 0) prefetchPage(state.pages[idx - 1]);
    if (idx !== -1 && idx < state.pages.length - 1) prefetchPage(state.pages[idx + 1]);
}

export async function switchPage(p, direction) {
    if (p === state.currentPage) return;
    if (state._switchingPage) return;
    state._switchingPage = true;
    try {
        const oldPage = state.currentPage;
        const dir = direction || (p > state.currentPage ? 'next' : 'prev');
        state.currentPage = p;
        if (!state.pages.includes(p)) {
            state.pages.push(p);
            state.pages.sort((a, b) => a - b);
        }

        const pageEl = $('#shortcutsPage');

        if (!_reduceMotion) {
            // 1) 当前页滑出（CSS 动画 0.16s，170ms 兜底足够）
            pageEl.classList.add('no-transition');
            pageEl.classList.remove('slide-in-left', 'slide-in-right', 'slide-out-left', 'slide-out-right');
            void pageEl.offsetWidth;
            pageEl.classList.remove('no-transition');
            pageEl.classList.add(dir === 'next' ? 'slide-out-left' : 'slide-out-right');

            await waitTransition(pageEl, 170);
        }

        // 2) 获取目标页数据（优先缓存），并强制符合当前网格容量
        let targetShortcuts;
        if (state.shortcutCache[p]) {
            targetShortcuts = state.shortcutCache[p];
        } else {
            try {
                targetShortcuts = await apiGet(API.shortcuts + '?action=list&page=' + p);
            } catch (e) { targetShortcuts = []; }
            state.shortcutCache[p] = targetShortcuts;
        }
        state.shortcuts = await enforcePageCapacity(targetShortcuts, p);

        // 3) 渲染新内容（此时透明度为 0，无视觉跳动）
        renderShortcuts();
        renderPages();

        // 4) 新页从反方向滑入：不等待动画结束即释放切换锁，
        //    连续翻页不会被动画串行阻塞（卡顿感的主要来源）
        if (!_reduceMotion) {
            pageEl.classList.add('no-transition');
            pageEl.classList.remove('slide-out-left', 'slide-out-right');
            pageEl.classList.add(dir === 'next' ? 'slide-in-right' : 'slide-in-left');
            void pageEl.offsetWidth;
            pageEl.classList.remove('no-transition');
            pageEl.classList.remove('slide-in-left', 'slide-in-right');
        }
        cacheState();

        // 后台清理：离开的空页（非首页）自动删除，保持分页干净连续。
        // 覆盖本次会话新建页和历史遗留空页；有内容的页只是不再标记为"新建"。
        if (oldPage && oldPage !== p && oldPage !== 1) {
            const oldCache = state.shortcutCache[oldPage];
            if (oldCache && oldCache.length === 0) {
                shiftPagesAfterRemoval(oldPage);
                apiPost(API.shortcuts + '?action=delete_page', { page: oldPage }).catch(() => {});
                renderPages();
            } else if (oldCache && oldCache.length > 0) {
                state._newPages.delete(oldPage); // 已有内容：不再是"新建空页"
            }
        }

        // 预加载相邻页，让下次切换更顺滑
        prefetchAdjacentPages();
    } finally {
        state._switchingPage = false;
    }
}

export async function loadShortcuts() {
    state._cardAllAt = 0; // 快捷方式数据变更：卡片模式全量缓存失效
    try {
        const raw = await apiGet(API.shortcuts + '?action=list&page=' + state.currentPage);
        state.shortcuts = await enforcePageCapacity(raw, state.currentPage);
        state.shortcutCache[state.currentPage] = state.shortcuts;
        // 始终与后端同步分页列表：保证空页不残留、页号连续
        let pages = await apiGet(API.shortcuts + '?action=pages');
        const isSeq = pages.every((p, i) => p === i + 1);
        if (!isSeq) {
            // 页号有空洞（如删页遗留的 [1,3,4]）：后端收缩为 1..N（不重排每页内容）
            const oldPages = pages.slice();
            await apiPost(API.shortcuts + '?action=compact_pages', {});
            pages = await apiGet(API.shortcuts + '?action=pages');
            // 页号已变：重映射当前页、缓存全部失效后重取
            const idx = oldPages.indexOf(state.currentPage);
            if (idx >= 0) state.currentPage = idx + 1;
            state.shortcutCache = {};
            const raw2 = await apiGet(API.shortcuts + '?action=list&page=' + state.currentPage);
            state.shortcuts = await enforcePageCapacity(raw2, state.currentPage);
            state.shortcutCache[state.currentPage] = state.shortcuts;
        }
        // 当前页在库里不存在（刷新后残留的已删空页）：回到最后一个真实页
        if (!pages.includes(state.currentPage) && !state._newPages.has(state.currentPage)) {
            state.currentPage = pages[pages.length - 1];
            const raw3 = await apiGet(API.shortcuts + '?action=list&page=' + state.currentPage);
            state.shortcuts = await enforcePageCapacity(raw3, state.currentPage);
            state.shortcutCache[state.currentPage] = state.shortcuts;
        }
        // 当前页是刚「+」出来的新页（库里还没数据）时保留在列表中
        if (!pages.includes(state.currentPage)) pages.push(state.currentPage);
        state.pages = pages;
    } catch (e) {
        /* 保留现有内容（可能来自首屏缓存）：瞬时网络抖动不应把已渲染的网格清空。
         * 初始无缓存时 state.shortcuts 本即为空数组，渲染空态即可。
         * 但 state.pages 必须兜底清空——否则 localStorage 里的脏缓存会让底部出现
         * 远超真实页数的页码点（用户截图反映的就是这种空页残留）。 */
        state.pages = [1];
        if (state.currentPage !== 1) state.currentPage = 1;
    }
    renderPages();
    /* 刷新防闪屏：服务器数据与已渲染内容一致时跳过整树重建（DOM/已加载图标原样保留）。
     * 卡片模式交给 renderCardViewAll 内部做同样的指纹比对。 */
    if (state.settings.display_mode === 'card') {
        renderShortcuts();
    } else if (state._renderedFp !== gridRenderFp()) {
        renderShortcuts();
    }
    if (window.requestIdleCallback) {
        requestIdleCallback(prefetchAdjacentPages, { timeout: 800 });
    } else {
        setTimeout(prefetchAdjacentPages, 300);
    }
}


